import { getNavigationPath, NavigationEntityType } from '@AppModels/hooks/useAINavigationResolver';
import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { StickToBottomContext } from 'use-stick-to-bottom';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useCeteraAIDisclaimer } from '../../../../models/hooks/useCeteraAIDisclaimer';
import { useEventLogging } from '../../../../models/Logging';
import { searchRequestToQueryStringParams } from '../../../../models/UiUtils';
import { useAdminAccountQuery } from '../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	getLastAIConversationChatMessage,
	invalidateAIConversationChatMessage,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	prependAIConversationToFilterCache,
	updateAIConversationInFilterCache,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import {
	AI_CONVERSATION_TITLE_MAX_LENGTH,
	truncateToConversationTitle,
	useUpdateAIConversationTitleMutation,
} from '../../../../queries/AIConversation/useUpdateAIConversationTitleMutation';
import { FullScreenModalViewModel } from '../../../../viewmodels/AppViewModels';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { AIConversationMessage } from '../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputAttachmentButton,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../components-v2/ai/PromptInput';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILES,
	AI_CHAT_MAX_FILE_SIZE,
	PromptInputAttachments,
	useAIChatAttachments,
} from '../../../components-v2/ai/PromptInput/PromptInputAttachments';
import { AI_THINKING_MESSAGES, ThinkingIndicator } from '../../../components-v2/ai/ThinkingIndicator';
import { Button } from '../../../components-v2/Button';
import { Avatar } from '@HoudiniUI/Avatar';
import { CloseButton } from '../../../components/CloseButton';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ChevronLeft } from '../../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../../components/svgs/icons/ChevronRight';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { HistoryIcon } from '../../../components/svgs/icons/HistoryIcon';
import { PlusIcon, PlusIcon2 } from '../../../components/svgs/icons/PlusIcon';
import { AnimatedSparkleIcon } from '../../../components/ai/AnimatedSparkleIcon';
import { SparkleIcon } from '../../../components/svgs/icons/SparkleIcon';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { ROUTES } from '../../../constants/routes';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { ConversationHistoryPanel } from './ConversationHistoryPanel';
import { s } from './styles';
import { applyAIStreamEventToCache } from './utils';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../components-v2/Combobox';
import { UserComboboxAvatarItem } from '../../../components/UserCombobox';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import { getDisplayName } from '../../../../models/NameUtils';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '@HoudiniUI/InputGroup';
import { create } from 'zustand';
import { StreamingStatus } from '../../../../hooks/useStreamingResponseMutation';
import { events, ServerSentEventMessage } from 'fetch-event-stream';

const KIT_CONVERSATION_STARTER_LABEL = 'Craft Keep-in-Touch Email';
const GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT = `GoogleBusinessReviewReplyAssistant`;
export const UNIVERSAL_ASSISTANT_NAME = `SiteWideAgent`;
const ADMIN_AGENT_NAME = `AdminAgent`;
const SUCCESS_MEETING_PREP_AGENT_NAME = `SuccessMeetingPrepAgent`;
const SURVEY_BUILDER_AGENT_NAME = `SurveyBuilderAgent`;

// Shared between each assistant's child component and its error boundary so the
// boundary can write to the exact same react-query cache key the child reads from.
const conversationTypeFilter = (type: Api.AIConversationType): Api.AIConversationFilter => ({
	criteria: [{ property: Api.AIConversationFilterProperty.ConversationType, value: type }],
});
const SITEWIDE_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.SiteWide);
const ADMIN_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.Admin);
const SUCCESS_MEETING_PREP_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.SuccessMeetingPrep);
const SURVEY_BUILDER_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.SurveyBuilder);

export const BRIEF_ASSISTANT_NAME = `EmailExecBriefChatAgent`;

const BRIEF_STARTER_PROMPTS = [
	'What needs a reply today?',
	'Summarize this brief in three bullets',
	'Draft a reply to the most urgent item',
	'Add the follow-ups as action items',
	'How does this compare with last week?',
];

const briefConversationFilter = (digestId: string): Api.AIConversationFilter => ({
	criteria: [
		{ property: Api.AIConversationFilterProperty.ResourceId, value: digestId },
		{ property: Api.AIConversationFilterProperty.AssistantName, value: BRIEF_ASSISTANT_NAME },
	],
});

// FE only type for handling error messages in chat
export interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}

function CampaignComposerAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.chatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar
						imgSrc={WorkingLionImageUrl}
						fallbackText='Leo Avatar'
						styles={[s.leoAvatar]}
						fallbackStyles={[s.leoAvatar]}
					/>
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className={css(s.plusIconButton)}
								style={onClose ? { marginRight: '24px' } : undefined}
								onClick={onNewChat}
							>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

const useCreateSeededSiteWideConversation = (impersonationContext?: Api.IImpersonationContext) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useCreateSeededSiteWideConversation');
	const { mutateAsync } = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});
	return async (firstMessageText: string) => {
		const created = await mutateAsync({ assistantName: UNIVERSAL_ASSISTANT_NAME, impersonationContext });
		if (!created?.id) {
			return null;
		}
		prependAIConversationToFilterCache(
			{ filter: SITEWIDE_CONVERSATION_FILTER },
			{ ...created, title: created.title || truncateToConversationTitle(firstMessageText) }
		);
		return created;
	};
};

const useAssistantConversation = (
	assistantName: string,
	isOpen: boolean,
	impersonationContext?: Api.IImpersonationContext
) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`useAssistantConversation-${assistantName}`, error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiAssistantCreateConversationMutation.data),
		id: aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
	});
	useEffect(() => {
		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, assistantName, impersonationContext]);
	return {
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
	};
};

export function AIChatAssistantMessage({
	children,
	isThinking = false,
}: {
	children: ReactNode;
	isThinking?: boolean;
}) {
	return (
		<div className={css(s.assitantMessage, isThinking && s.assitantMessageThinking)}>
			{isThinking ? (
				<AnimatedSparkleIcon styles={[s.assistantMessageSparkle]} />
			) : (
				<SparkleIcon styles={[s.assistantMessageSparkle]} />
			)}
			<div className={css(s.assistantMessageContent)}>{children}</div>
		</div>
	);
}

export function AIChatUserMessage({ children }: { children: ReactNode }) {
	return <div className={css(s.userMessage)}>{children}</div>;
}

interface Params {
	conversationId: string;
	chatRequest: Api.AIChatRequest;
	impersonationContext?: Api.IImpersonationContext;
}

type ActiveStreamStatus = Exclude<StreamingStatus, 'idle'>;
interface AIStreamRecord {
	status: ActiveStreamStatus;
	error: Error | null;
}
interface AIStreamState {
	streams: Record<string, AIStreamRecord>;
	actions: {
		start: (conversationId: string) => void;
		startStreaming: (conversationId: string) => void;
		finish: (conversationId: string) => void;
		error: (conversationId: string, error: Error) => void;
	};
}

const useAIStreamStateStore = create<AIStreamState>(set => ({
	streams: {},
	actions: {
		start: (conversationId: string) =>
			set(state => ({ streams: { ...state.streams, [conversationId]: { status: 'loading', error: null } } })),
		startStreaming: (conversationId: string) =>
			set(state => ({ streams: { ...state.streams, [conversationId]: { status: 'streaming', error: null } } })),
		finish: (conversationId: string) =>
			set(state => {
				if (!state.streams[conversationId]) {
					return state;
				}
				const { [conversationId]: _removed, ...rest } = state.streams;
				return { streams: rest };
			}),
		error: (conversationId: string, error: Error) =>
			set(state => ({ streams: { ...state.streams, [conversationId]: { status: 'error', error } } })),
	},
}));

const useAIStreamStateStreams = () => useAIStreamStateStore(state => state.streams);
export const useAIStreamConversationState = (conversationId: string) => {
	return useAIStreamStateStore(state => state.streams[conversationId]);
};

const abortControllers = new Map<string, AbortController>();

async function startAIStream({
	conversationId,
	fetchResponse,
	onEvent,
	onError,
	onSuccess,
}: {
	conversationId: string;
	fetchResponse: () => Promise<Response>;
	onEvent: (event: ServerSentEventMessage) => void;
	onError?: (error: Error) => void;
	onSuccess?: () => void;
}) {
	const conversationStreamState = useAIStreamStateStore.getState().streams[conversationId];
	if (conversationStreamState?.status === 'loading' || conversationStreamState?.status === 'streaming') {
		return;
	}
	const aiStreamStateActions = useAIStreamStateStore.getState().actions;

	const controller = new AbortController();
	abortControllers.set(conversationId, controller);
	aiStreamStateActions.start(conversationId);

	try {
		const response = await fetchResponse();
		const stream = events(response, controller.signal);
		let hasStreamed = false;
		for await (const event of stream) {
			if (event.event === 'Heartbeat') {
				continue;
			}
			if (!hasStreamed) {
				hasStreamed = true;
				aiStreamStateActions.startStreaming(conversationId);
			}
			onEvent(event);
		}

		abortControllers.delete(conversationId);
		aiStreamStateActions.finish(conversationId);
		onSuccess?.();
	} catch (e) {
		const error = e as Error;
		abortControllers.delete(conversationId);
		aiStreamStateActions.error(conversationId, error);
		onError?.(error);
	}
}

function abortAIStream(conversationId?: string) {
	if (!conversationId) {
		return;
	}
	abortControllers.get(conversationId)?.abort();
	abortControllers.delete(conversationId);
	useAIStreamStateStore.getState().actions.finish(conversationId);
}

export function abortAllAIStreamsAndClear() {
	abortControllers.forEach(controller => controller.abort());
	abortControllers.clear();
	useAIStreamStateStore.setState({ streams: {} });
}

export const useAIAssistantStreamingMutation = ({
	assistantName,
	conversationId,
	onError,
	onSuccess,
}: {
	assistantName: string;
	conversationId?: string;
	onError?: (error: Error, params: Params) => void;
	onSuccess?: (params: Params) => void;
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const queryClient = useQueryClient();
	const logger = useEventLogging('useAIAssistantStreamingMutation');
	const [lastStartedConversationId, setLastStartedConversationId] = useState<string | null>(null);
	const scopedConversationId = conversationId ?? lastStartedConversationId ?? undefined;
	const streams = useAIStreamStateStreams();
	const status: StreamingStatus = scopedConversationId ? (streams[scopedConversationId]?.status ?? 'idle') : 'idle';
	const error = scopedConversationId ? (streams[scopedConversationId]?.error ?? null) : null;

	const mutate = async (params: Params) => {
		setLastStartedConversationId(params.conversationId);
		const queryKey = getConversationChatMessageQueryKey({
			id: params.conversationId,
			impersonationContext: params.impersonationContext,
		});

		return startAIStream({
			conversationId: params.conversationId,
			fetchResponse: () =>
				new Promise<Response>((resolve, reject) =>
					userSession.webServiceHelper.callWebServiceRawResponse(
						Api.ImpersonationBroker.composeApiUrl({
							urlPath: `ai/assistant/${assistantName}/conversation/${params.conversationId}/stream`,
							impersonationContext: params.impersonationContext,
						}),
						'POST',
						params.chatRequest,
						resolve,
						reject
					)
				),
			onEvent: event => {
				if (event.event === 'Error') {
					logger.logEvent('AssistantChatStreamEvent-Error', {
						message: event.data,
					});
				}
				applyAIStreamEventToCache({
					event,
					queryClient,
					queryKey,
					chatRequest: params.chatRequest,
				});
			},
			onError: streamError => {
				console.error(streamError);
				logger.logApiError('AssistantChatStreamRequest-Error', streamError as Api.IOperationResultNoValue);
				errorMessages.pushApiError(streamError, 'Error while streaming AI assistant response');
				onError?.(streamError, params);
			},
			onSuccess: () => {
				const lastMessage = getLastAIConversationChatMessage(params.conversationId, params.impersonationContext);
				if (!(lastMessage as AIChatMessageError)?.error) {
					invalidateAIConversationChatMessage(params.conversationId);
				}
				onSuccess?.(params);
			},
		});
	};

	const reset = () => abortAIStream(scopedConversationId);

	return {
		mutate,
		reset,
		status,
		error,
		isLoading: status === 'loading',
		isStreaming: status === 'streaming',
		isError: status === 'error',
	};
};

export function removeErrorMessageFromConversationCache(
	conversationId: string,
	impersonationContext?: Api.IImpersonationContext
) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if ((messages.at(-1) as AIChatMessageError)?.error) {
		reactQueryClient.setQueryData(conversationQueryKey, messages.slice(0, -1));
	}
}

export function AIChatAssistantMessageError({ onTryAgain }: { onTryAgain: () => void }) {
	return (
		<AIChatAssistantMessage>
			<div className={css(s.errorMessageContainer)}>
				<div>Error</div>
				<p className={css(s.errorMessageDetails)}>Something went wrong with the assistant. Please try again.</p>
				<Button variant='destructive-secondary' size='sm' onClick={onTryAgain} styles={[s.errorMessageButton]}>
					Try Again
				</Button>
			</div>
		</AIChatAssistantMessage>
	);
}

export function GoogleReviewReplyAssistantChat({
	isOpen,
	onUseGoogleReviewReply,
	context,
	impersonationContext,
	onClose,
}: {
	isOpen: boolean;
	onUseGoogleReviewReply: (reply: string, sessionId?: string) => void;
	context?: {
		review: Api.IGoogleBusinessReview;
		companyName: string;
		reply: string;
	};
	impersonationContext?: Api.IImpersonationContext;
	onClose?: () => void;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const { conversationId, aiConversationChatMessageQuery } = useAssistantConversation(
		GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
		isOpen,
		impersonationContext
	);
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
	});
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: context
					? [
							{
								_type: 'GoogleReviewAIContext',
								reviewedCompanyName: context.companyName,
								review: context.review,
								reply: context.reply,
							} as Api.GoogleReviewAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	const handleCraftReplyMessageClick = () => {
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				action: {
					_type: 'GoogleReviewReplyAction',
					reviewId: context.review.id,
				} as Api.GoogleReviewReplyAction,
				contexts: [
					{
						_type: 'GoogleReviewAIContext',
						reviewedCompanyName: context.companyName,
						review: context.review,
						reply: context.reply,
					} as Api.GoogleReviewAIContext,
				],
			} as Api.AIActionRequest,
			impersonationContext,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!conversationId) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I can help you craft a response. Do you have any specific instructions for
						this message?
					</AIChatAssistantMessage>
					{context?.review &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<button className={css(bs.ctaButtonReverse, s.aiKitCta)} onClick={handleCraftReplyMessageClick}>
							<span>Craft Reply Message</span> <ChevronRight />
						</button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Add Google Review Reply Prompt' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

export function UniversalAssistantLayout({
	onClose,
	onNewChat,
	onBack,
	children,
	title = 'Ask Lev',
	titleContent,
	historyControls,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	onBack?: () => void;
	children: ReactNode;
	title?: string;
	titleContent?: ReactNode;
	historyControls?: ReactNode;
}) {
	return (
		<div className={css(s.universalChatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					{onBack ? (
						<Button variant='ghost' size='sm' onClick={onBack} styles={[s.headerBackButton]} aria-label='Back'>
							<ChevronLeft width={20} height={20} />
						</Button>
					) : null}
					<Avatar
						imgSrc={WorkingLionImageUrl}
						fallbackText='Leo Avatar'
						styles={[s.leoAvatar]}
						fallbackStyles={[s.leoAvatar]}
					/>
					{titleContent ?? <span className={css(s.chatWindowHeaderTitle)}>{title}</span>}
				</div>
				<div className={css(s.chatWindowHeaderActions)}>
					{historyControls}
					{onNewChat ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button variant='ghost' size='sm' onClick={onNewChat} styles={[s.plusIconButton]} aria-label='New Chat'>
									<PlusIcon2 />
								</Button>
							</TooltipTrigger>
							<TooltipContent>New Chat</TooltipContent>
						</Tooltip>
					) : null}
				</div>
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

type AssistantConversationSelection = { type: 'newest' } | { type: 'draft' } | { type: 'conversation'; id: string };

type ConversationDrawerState = {
	conversations: Record<string, AssistantConversationSelection>;
	actions: {
		startNewChat: (assistantName: string) => void;
		selectConversation: (assistantName: string, conversationId: string) => void;
		submitPrompt: (assistantName: string, conversationId: string) => void;
	};
};

const NEWEST: AssistantConversationSelection = { type: 'newest' };
const DRAFT: AssistantConversationSelection = { type: 'draft' };

const useConversationDrawerStateStore = create<ConversationDrawerState>(set => ({
	conversations: {
		[UNIVERSAL_ASSISTANT_NAME]: NEWEST,
	},
	actions: {
		startNewChat: (assistantName: string) =>
			set(state => ({
				conversations: {
					...state.conversations,
					[assistantName]: DRAFT,
				},
			})),
		selectConversation: (assistantName: string, conversationId: string) =>
			set(state => ({
				conversations: {
					...state.conversations,
					[assistantName]: { type: 'conversation', id: conversationId },
				},
			})),
		submitPrompt: (assistantName: string, conversationId: string) =>
			set(state => ({
				conversations: {
					...state.conversations,
					[assistantName]: { type: 'conversation', id: conversationId },
				},
			})),
	},
}));
const useConversationDrawerAssistantState = (assistantName: string) =>
	useConversationDrawerStateStore(state => state.conversations[assistantName]);
const useConversationDrawerStateActions = () => useConversationDrawerStateStore(state => state.actions);

export const resetConversationDrawerState = () =>
	useConversationDrawerStateStore.setState({ conversations: { [UNIVERSAL_ASSISTANT_NAME]: NEWEST } });

export function UniversalAssistantChat({
	onUseAIEmail,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onUseContact,
	onUseContactList,
	onNavigate,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: UniversalAssistantChatProps) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const logger = useEventLogging('UniversalAssistantChat');
	const fullscreenModal = useFullscreenModal();
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		onUseBlogContent(title, content);
		showCeteraDisclaimer();
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationDrawerState = useConversationDrawerAssistantState(UNIVERSAL_ASSISTANT_NAME);
	const conversationDrawerActions = useConversationDrawerStateActions();
	const [isBrowsingHistory, setIsBrowsingHistory] = useState(false);

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: SITEWIDE_CONVERSATION_FILTER,
	});
	const conversations = aiConversationFilterQuery.data;
	const conversationId =
		conversationDrawerState.type === 'conversation'
			? conversationDrawerState.id
			: conversationDrawerState.type === 'newest'
				? conversations?.[0]?.id
				: undefined;
	const selectedConversation = conversationId ? conversations?.find(x => x.id === conversationId) : undefined;
	const [titleDraft, setTitleDraft] = useState<string | null>(null);
	const titleValue = titleDraft ?? selectedConversation?.title ?? '';
	const [lastConversationId, setLastConversationId] = useState(conversationId);
	if (conversationId !== lastConversationId) {
		setLastConversationId(conversationId);
		setTitleDraft(null);
	}

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId,
		staleTime: Infinity,
	});
	const createSeededConversation = useCreateSeededSiteWideConversation(impersonationContext);
	const { mutate: renameConversation } = useUpdateAIConversationTitleMutation({
		onSuccess: conversation => {
			updateAIConversationInFilterCache({ filter: SITEWIDE_CONVERSATION_FILTER }, conversation);
			setTitleDraft(null);
		},
		onError: error => {
			setTitleDraft(null);
			toast.error('Error occurred renaming conversation');
			logger.logApiError(`Rename-SiteWide-Conversation-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		conversationDrawerActions.startNewChat(UNIVERSAL_ASSISTANT_NAME);
		setIsBrowsingHistory(false);
	};

	const handleSelectConversation = (selectedId: string) => {
		conversationDrawerActions.selectConversation(UNIVERSAL_ASSISTANT_NAME, selectedId);
		setIsBrowsingHistory(false);
	};

	const handleRenameSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		const title = new FormData(ev.currentTarget).get('conversationTitle')?.toString().trim();
		if (!title || !conversationId || title === selectedConversation?.title) {
			setTitleDraft(null);
			return;
		}
		renameConversation({ conversationId, title, impersonationContext });
	};

	// Scoped to the selected conversation, so a stream still running in another one leaves this
	// composer alone.
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
		conversationId,
	});
	const isThisConversationStreaming =
		aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming;
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (isThisConversationStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = async ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (isThisConversationStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		let targetConversationId = conversationId;
		if (!targetConversationId) {
			try {
				const created = await createSeededConversation(text);
				if (!created) {
					return;
				}
				targetConversationId = created.id;
				conversationDrawerActions.submitPrompt(UNIVERSAL_ASSISTANT_NAME, targetConversationId);
			} catch {
				return;
			}
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(targetConversationId, impersonationContext);
		const messagesQueryKey = getConversationChatMessageQueryKey({
			id: targetConversationId,
			impersonationContext,
		});
		// A history fetch is in flight whenever the user sends into a conversation they just switched
		// to. Left alone it resolves mid-stream and overwrites both this optimistic message and every
		// token written so far, which reads as the response stalling until the post-stream refetch
		// heals it.
		await queryClient.cancelQueries({ queryKey: messagesQueryKey });
		queryClient.setQueryData(messagesQueryKey, (prevMessages: Api.AIChatMessage[]) => [
			...(prevMessages ?? []),
			{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
		]);
		aiAssistantStreamingMutation.mutate({
			conversationId: targetConversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
				impersonationContext,
			} as Api.AIMessageRequest,
		});
		resetAttachments();
	};

	const historyControls = (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => setIsBrowsingHistory(true)}
					styles={[s.plusIconButton]}
					aria-label='Previous conversations'
				>
					<HistoryIcon fillColor={brandPrimary} />
				</Button>
			</TooltipTrigger>
			<TooltipContent>Previous conversations</TooltipContent>
		</Tooltip>
	);

	if (aiConversationFilterQuery.isLoading) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	if (isBrowsingHistory) {
		return (
			<UniversalAssistantLayout
				onClose={onClose}
				onNewChat={handleNewChatClick}
				onBack={() => setIsBrowsingHistory(false)}
				titleContent={<span className={css(s.chatWindowHeaderTitle)}>Conversations</span>}
			>
				<ConversationHistoryPanel
					conversations={conversations}
					isLoading={aiConversationFilterQuery.isLoading}
					selectedConversationId={conversationId}
					onSelectConversation={handleSelectConversation}
				/>
			</UniversalAssistantLayout>
		);
	}

	const titleContent = conversationId ? (
		<form className={css(s.headerTitleForm)} onSubmit={handleRenameSubmit}>
			<InputGroupV2 styles={[s.headerTitleInputGroup]}>
				<InputGroupInput
					name='conversationTitle'
					value={titleValue}
					onChange={ev => setTitleDraft(ev.target.value)}
					maxLength={AI_CONVERSATION_TITLE_MAX_LENGTH}
					styles={[s.headerTitleInput]}
					aria-label='Conversation title'
					onBlur={ev => ev.currentTarget.form?.requestSubmit()}
					placeholder='Name this conversation'
				/>
				<InputGroupAddon align='inlineEnd' styles={[s.headerTitleEditAddon]}>
					<EditItemPenIcon fillColor={brandPrimary} />
				</InputGroupAddon>
			</InputGroupV2>
		</form>
	) : undefined;

	return (
		<UniversalAssistantLayout
			onClose={onClose}
			onNewChat={handleNewChatClick}
			titleContent={titleContent}
			historyControls={historyControls}
		>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					{!aiConversationChatMessageQuery.data?.length ? (
						<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					) : null}
					{renderConversationStarters &&
					conversationId &&
					!aiConversationChatMessageQuery.data?.length &&
					!isThisConversationStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onNavigate={onNavigate}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{isThisConversationStreaming && aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={isThisConversationStreaming ? aiAssistantStreamingMutation.status : 'idle'}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

export interface BriefAssistantProps {
	digestId: string;
	initialAssistantPlaceholderMessage?: string;
	onClose?: () => void;
	impersonationContext?: Api.IImpersonationContext;
}

export function BriefAssistant({
	digestId,
	initialAssistantPlaceholderMessage = 'Ask me anything about this brief — I can also draft replies or add action items.',
	impersonationContext,
}: BriefAssistantProps) {
	const id = useId();
	const queryClient = useQueryClient();
	const logger = useEventLogging('BriefAssistant');
	const conversationRef = useRef<StickToBottomContext>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const errorMessages = useErrorMessages();
	const conversationFilter = useMemo(() => briefConversationFilter(digestId), [digestId]);

	const drawerStateKey = `${BRIEF_ASSISTANT_NAME}:${digestId}`;
	const conversationDrawerState = useConversationDrawerAssistantState(drawerStateKey) ?? NEWEST;
	const conversationDrawerActions = useConversationDrawerStateActions();
	const [isBrowsingHistory, setIsBrowsingHistory] = useState(false);

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: conversationFilter,
		impersonationContext,
	});
	const conversations = aiConversationFilterQuery.data;
	const conversationId =
		conversationDrawerState.type === 'conversation'
			? conversationDrawerState.id
			: conversationDrawerState.type === 'newest'
				? conversations?.[0]?.id
				: undefined;
	const selectedConversation = conversationId ? conversations?.find(x => x.id === conversationId) : undefined;
	const [titleDraft, setTitleDraft] = useState<string | null>(null);
	const titleValue = titleDraft ?? selectedConversation?.title ?? '';
	const [lastConversationId, setLastConversationId] = useState(conversationId);
	if (conversationId !== lastConversationId) {
		setLastConversationId(conversationId);
		setTitleDraft(null);
	}

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId,
		staleTime: Infinity,
		impersonationContext,
	});

	const { mutateAsync: createConversation } = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('Create-Brief-Assistant-Error', error);
		},
	});
	const { mutate: renameConversation } = useUpdateAIConversationTitleMutation({
		onSuccess: conversation => {
			updateAIConversationInFilterCache({ filter: conversationFilter, impersonationContext }, conversation);
			setTitleDraft(null);
		},
		onError: error => {
			setTitleDraft(null);
			toast.error('Error occurred renaming conversation');
			logger.logApiError('Rename-Brief-Conversation-Error', error);
		},
	});

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: BRIEF_ASSISTANT_NAME,
		conversationId,
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
	});
	const isThisConversationStreaming =
		aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming;
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;

	const briefContext = (): Api.ResourceAIContext[] => [
		{
			_type: 'ResourceAIContext',
			resourceType: Api.EMAIL_EXEC_DIGEST_RESOURCE_TYPE,
			resourceId: digestId,
		} as Api.ResourceAIContext,
	];

	const handleNewChatClick = () => {
		conversationDrawerActions.startNewChat(drawerStateKey);
		setIsBrowsingHistory(false);
	};

	const handleSelectConversation = (selectedId: string) => {
		conversationDrawerActions.selectConversation(drawerStateKey, selectedId);
		setIsBrowsingHistory(false);
	};

	const handleRenameSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		const title = new FormData(ev.currentTarget).get('conversationTitle')?.toString().trim();
		if (!title || !conversationId || title === selectedConversation?.title) {
			setTitleDraft(null);
			return;
		}
		renameConversation({ conversationId, title, impersonationContext });
	};

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: { ...chatRequest, contexts: briefContext() } as Api.AIChatRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (isThisConversationStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};

	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};

	const submitText = async (text: string) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (isThisConversationStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		let targetConversationId = conversationId;
		if (!targetConversationId) {
			let created: Api.AIConversation | null = null;
			try {
				created = await createConversation({ assistantName: BRIEF_ASSISTANT_NAME, impersonationContext });
			} catch {
				return;
			}
			if (!created?.id) {
				return;
			}
			targetConversationId = created.id;
			prependAIConversationToFilterCache(
				{ filter: conversationFilter, impersonationContext },
				{ ...created, title: created.title || truncateToConversationTitle(text) }
			);
			conversationDrawerActions.submitPrompt(drawerStateKey, targetConversationId);
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(targetConversationId, impersonationContext);
		const messagesQueryKey = getConversationChatMessageQueryKey({
			id: targetConversationId,
			impersonationContext,
		});
		await queryClient.cancelQueries({ queryKey: messagesQueryKey });
		queryClient.setQueryData(messagesQueryKey, (prevMessages: Api.AIChatMessage[]) => [
			...(prevMessages ?? []),
			{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
		]);
		aiAssistantStreamingMutation.mutate({
			conversationId: targetConversationId,
			chatRequest: {
				chatMessages: [{ role: Api.AIChatRole.User, contents, id: null }],
				contexts: briefContext(),
				impersonationContext,
			} as Api.AIMessageRequest,
		});
		resetAttachments();
	};

	const handlePromptSubmit = ({ text }: PromptInputMessage) => submitText(text);

	const historyControls = (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => setIsBrowsingHistory(true)}
					styles={[s.plusIconButton]}
					aria-label='Previous conversations'
				>
					<HistoryIcon fillColor={brandPrimary} />
				</Button>
			</TooltipTrigger>
			<TooltipContent>Previous conversations</TooltipContent>
		</Tooltip>
	);

	if (aiConversationFilterQuery.isLoading) {
		return (
			<UniversalAssistantLayout onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	if (isBrowsingHistory) {
		return (
			<UniversalAssistantLayout
				onNewChat={handleNewChatClick}
				onBack={() => setIsBrowsingHistory(false)}
				titleContent={<span className={css(s.chatWindowHeaderTitle)}>Conversations</span>}
			>
				<ConversationHistoryPanel
					conversations={conversations}
					isLoading={aiConversationFilterQuery.isLoading}
					selectedConversationId={conversationId}
					onSelectConversation={handleSelectConversation}
				/>
			</UniversalAssistantLayout>
		);
	}

	const titleContent = conversationId ? (
		<form className={css(s.headerTitleForm)} onSubmit={handleRenameSubmit}>
			<InputGroupV2 styles={[s.headerTitleInputGroup]}>
				<InputGroupInput
					name='conversationTitle'
					value={titleValue}
					onChange={ev => setTitleDraft(ev.target.value)}
					maxLength={AI_CONVERSATION_TITLE_MAX_LENGTH}
					styles={[s.headerTitleInput]}
					aria-label='Conversation title'
					onBlur={ev => ev.currentTarget.form?.requestSubmit()}
					placeholder='Name this conversation'
				/>
				<InputGroupAddon align='inlineEnd' styles={[s.headerTitleEditAddon]}>
					<EditItemPenIcon fillColor={brandPrimary} />
				</InputGroupAddon>
			</InputGroupV2>
		</form>
	) : undefined;

	const showStarters = !aiConversationChatMessageQuery.data?.length && !isThisConversationStreaming;

	return (
		<UniversalAssistantLayout
			onNewChat={handleNewChatClick}
			titleContent={titleContent}
			historyControls={historyControls}
		>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					{!aiConversationChatMessageQuery.data?.length ? (
						<AIChatAssistantMessage>{initialAssistantPlaceholderMessage}</AIChatAssistantMessage>
					) : null}
					{showStarters ? (
						<div className={css(s.briefStarters)}>
							{BRIEF_STARTER_PROMPTS.map(prompt => (
								<Button variant='secondary' size='sm' key={prompt} onClick={() => submitText(prompt)}>
									{prompt}
								</Button>
							))}
						</div>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{isThisConversationStreaming && aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Ask about this brief…' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={isThisConversationStreaming ? aiAssistantStreamingMutation.status : 'idle'}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

export function UniversalAssistantChatWithinComposer({
	onUseAIEmail,
	onUseSocialContent,
	onUseBlogContent,
	onClose,
	emailContext,
	socialContext,
	blogContext,
	isOpen,
	impersonationContext,
}: {
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseBlogContent?: (title: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	socialContext?: { content: Api.IRawRichTextContentState };
	blogContext?: { title: string; content: Api.IRawRichTextContentState };
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const onUseSocialContentHandler = onUseSocialContent ?? defaultOnUseSocialContent(fullscreenModal);
	// Google revies are diabled because we don't have a path for it yet
	const onUseGoogleReviewReply: (reply: string) => void = undefined;
	const defaultBlogHandler = onUseBlogContent ?? defaultOnUseBlogContent(fullscreenModal);
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		defaultBlogHandler(title, content);
		showCeteraDisclaimer();
	};
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const request = buildContactsSearchRequest(contactList);
		const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const [conversationId, setConversationId] = useState<string | null>(null);
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(conversationId),
		id: conversationId ?? undefined,
		staleTime: Infinity,
	});
	const createSeededConversation = useCreateSeededSiteWideConversation(impersonationContext);

	const handleNewChatClick = () => {
		setConversationId(null);
	};

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
		conversationId: conversationId ?? undefined,
	});
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = async ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		let targetConversationId = conversationId;
		if (!targetConversationId) {
			try {
				const created = await createSeededConversation(text);
				if (!created) {
					return;
				}
				targetConversationId = created.id;
				setConversationId(targetConversationId);
			} catch {
				return;
			}
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(targetConversationId);
		const messagesQueryKey = getConversationChatMessageQueryKey({
			id: targetConversationId,
			impersonationContext,
		});
		await queryClient.cancelQueries({ queryKey: messagesQueryKey });
		queryClient.setQueryData(messagesQueryKey, (prevMessages: Api.AIChatMessage[]) => [
			...(prevMessages ?? []),
			{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
		]);
		aiAssistantStreamingMutation.mutate({
			conversationId: targetConversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
				contexts: (() => {
					const contexts = [
						...(emailContext
							? [
									{
										_type: 'EmailAIContext',
										subject: emailContext.subject,
										body: emailContext.body,
									} as Api.EmailAIContext,
								]
							: []),
						...(socialContext
							? [
									{
										_type: 'SocialMediaAIContext',
										content: socialContext.content,
									} as Api.SocialMediaAIContext,
								]
							: []),
						...(blogContext
							? [
									{
										_type: 'BlogAIContext',
										title: blogContext.title,
										content: blogContext.content,
									} as Api.BlogAIContext,
								]
							: []),
					];
					return contexts.length > 0 ? contexts : undefined;
				})(),
			} as Api.AIMessageRequest,
		});
		resetAttachments();
	};
	if (!isOpen) {
		return null;
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContentHandler}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={aiAssistantStreamingMutation.status}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

export function UniversalAssistantChatWithinSingleSendComposer({
	onUseAIEmail,
	onClose,
	emailContext,
	isOpen,
	impersonationContext,
	kitAIActionRequest,
}: {
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
	kitAIActionRequest: Api.AIActionRequest;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Lazy: no conversation is loaded or created until the user engages.
	const [conversationId, setConversationId] = useState<string | null>(null);

	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const request = buildContactsSearchRequest(contactList);
		const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(conversationId),
		id: conversationId ?? undefined,
		staleTime: Infinity,
	});

	const createSeededConversation = useCreateSeededSiteWideConversation(impersonationContext);

	const ensureConversation = async (firstMessageText: string): Promise<string | null> => {
		if (conversationId) return conversationId;
		const created = await createSeededConversation(firstMessageText);
		if (!created) {
			return null;
		}
		setConversationId(created.id);
		return created.id;
	};

	const handleNewChatClick = () => {
		setConversationId(null);
	};
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
	});
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) return;
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = async ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		const activeConversationId = await ensureConversation(text);
		if (!activeConversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(activeConversationId);
		const messagesQueryKey = getConversationChatMessageQueryKey({
			id: activeConversationId,
			impersonationContext,
		});
		await queryClient.cancelQueries({ queryKey: messagesQueryKey });
		queryClient.setQueryData(messagesQueryKey, (prevMessages: Api.AIChatMessage[]) => [
			...(prevMessages ?? []),
			{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
		]);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
				contexts: emailContext
					? [
							{
								_type: 'EmailAIContext',
								subject: emailContext.subject,
								body: emailContext.body,
							} as Api.EmailAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
		});
		resetAttachments();
	};
	const handleKITConversationStarterClick = async () => {
		const convId = await ensureConversation(KIT_CONVERSATION_STARTER_LABEL);
		if (!convId) {
			return;
		}
		aiAssistantStreamingMutation.mutate({
			conversationId: convId,
			chatRequest: kitAIActionRequest,
		});
	};
	if (!isOpen) {
		return null;
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<Button variant='secondary' styles={[bs.ml4, bs.mr4]} onClick={handleKITConversationStarterClick}>
							{KIT_CONVERSATION_STARTER_LABEL} <ChevronRight />
						</Button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId as string}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={aiAssistantStreamingMutation.status}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

/**
 * Maps string property names from AI SimpleFilter to ContactFilterCriteriaProperty enum values.
 * This is needed because the AI returns property names as strings, but the API expects enum values.
 */
const SIMPLE_FILTER_PROPERTY_MAP: Record<string, Api.ContactFilterCriteriaProperty> = {
	Tag: Api.ContactFilterCriteriaProperty.Tag,
	City: Api.ContactFilterCriteriaProperty.City,
	StateProvince: Api.ContactFilterCriteriaProperty.StateProvince,
	PostalCode: Api.ContactFilterCriteriaProperty.PostalCode,
	CompanyTag: Api.ContactFilterCriteriaProperty.CompanyTag,
	Name: Api.ContactFilterCriteriaProperty.Name,
	Company: Api.ContactFilterCriteriaProperty.Company,
	Source: Api.ContactFilterCriteriaProperty.Source,
	OwnedBy: Api.ContactFilterCriteriaProperty.OwnedBy,
	ConnectionType: Api.ContactFilterCriteriaProperty.ConnectionType,
	WithEmailAddress: Api.ContactFilterCriteriaProperty.WithEmailAddress,
	WithPhoneNumber: Api.ContactFilterCriteriaProperty.WithPhoneNumber,
	KeepInTouch: Api.ContactFilterCriteriaProperty.KeepInTouch,
	LastCommunicationDate: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
	UpcomingKeyDates: Api.ContactFilterCriteriaProperty.UpcomingKeyDates,
};

/** Maps SimpleFilter.op string values to CONTACT_FILTER_OPERATOR values. */
const SIMPLE_FILTER_OPERATOR_MAP: Record<string, Api.ContactFilterOperator> = {
	And: Api.CONTACT_FILTER_OPERATOR.And,
	Or: Api.CONTACT_FILTER_OPERATOR.Or,
	Not: Api.CONTACT_FILTER_OPERATOR.Not,
};

/**
 * Maps string property names (as sent by the AI backend) to ContactFilterCriteriaProperty enum values.
 * Needed because numeric-valued enum members serialize as strings from the AI ("Tag" → 9, "OwnedBy" → 3),
 * while string-valued members (City, StateProvince, PostalCode, CompanyTag) already match.
 */
const PROPERTY_NAME_TO_ENUM: Record<string, Api.ContactFilterCriteriaProperty> = {
	All: Api.ContactFilterCriteriaProperty.All,
	Connections: Api.ContactFilterCriteriaProperty.Connections,
	CreatedBy: Api.ContactFilterCriteriaProperty.CreatedBy,
	OwnedBy: Api.ContactFilterCriteriaProperty.OwnedBy,
	PrivateContacts: Api.ContactFilterCriteriaProperty.PrivateContacts,
	KeepInTouch: Api.ContactFilterCriteriaProperty.KeepInTouch,
	TagAlert: Api.ContactFilterCriteriaProperty.TagAlert,
	Name: Api.ContactFilterCriteriaProperty.Name,
	Company: Api.ContactFilterCriteriaProperty.Company,
	Tag: Api.ContactFilterCriteriaProperty.Tag,
	Email: Api.ContactFilterCriteriaProperty.Email,
	UpcomingKeyDates: Api.ContactFilterCriteriaProperty.UpcomingKeyDates,
	UpcomingKeyDatesNotScheduled: Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled,
	WithEmailAddress: Api.ContactFilterCriteriaProperty.WithEmailAddress,
	WithoutEmailAddresses: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
	WithoutTags: Api.ContactFilterCriteriaProperty.WithoutTags,
	InProgressAutomations: Api.ContactFilterCriteriaProperty.InProgressAutomations,
	OnHoldAutomations: Api.ContactFilterCriteriaProperty.OnHoldAutomations,
	PreviousAutomations: Api.ContactFilterCriteriaProperty.PreviousAutomations,
	Policy: Api.ContactFilterCriteriaProperty.Policy,
	KeyDate: Api.ContactFilterCriteriaProperty.KeyDate,
	KeyDateKind: Api.ContactFilterCriteriaProperty.KeyDateKind,
	KeyDateYear: Api.ContactFilterCriteriaProperty.KeyDateYear,
	KeyDateSuppressList: Api.ContactFilterCriteriaProperty.KeyDateSuppressList,
	KeyDateSuppressDate: Api.ContactFilterCriteriaProperty.KeyDateSuppressDate,
	Archived: Api.ContactFilterCriteriaProperty.Archived,
	CapableOf: Api.ContactFilterCriteriaProperty.CapableOf,
	PhoneNumber: Api.ContactFilterCriteriaProperty.PhoneNumber,
	Source: Api.ContactFilterCriteriaProperty.Source,
	WithMailingAddress: Api.ContactFilterCriteriaProperty.WithMailingAddress,
	ConnectionTypeOrOwner: Api.ContactFilterCriteriaProperty.ConnectionTypeOrOwner,
	WithPhoneNumber: Api.ContactFilterCriteriaProperty.WithPhoneNumber,
	WithoutPhoneNumber: Api.ContactFilterCriteriaProperty.WithoutPhoneNumber,
	HasDuplicates: Api.ContactFilterCriteriaProperty.HasDuplicates,
	ConnectionType: Api.ContactFilterCriteriaProperty.ConnectionType,
	CreationDate: Api.ContactFilterCriteriaProperty.CreationDate,
	KeyFactOwnedBy: Api.ContactFilterCriteriaProperty.KeyFactOwnedBy,
	NamePrefix: Api.ContactFilterCriteriaProperty.NamePrefix,
	LastCommunicationDate: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
	City: Api.ContactFilterCriteriaProperty.City,
	StateProvince: Api.ContactFilterCriteriaProperty.StateProvince,
	PostalCode: Api.ContactFilterCriteriaProperty.PostalCode,
	CompanyTag: Api.ContactFilterCriteriaProperty.CompanyTag,
	// The backend C# enum assigns numeric values (39-42) to properties that the frontend
	// enum defines as string values. Map the backend's numeric values to the frontend strings.
	39: Api.ContactFilterCriteriaProperty.City,
	40: Api.ContactFilterCriteriaProperty.StateProvince,
	41: Api.ContactFilterCriteriaProperty.PostalCode,
	42: Api.ContactFilterCriteriaProperty.CompanyTag,
};

/**
 * Normalizes contact filter criteria from the AI backend by mapping string property names
 * to their corresponding ContactFilterCriteriaProperty enum values.
 * Recursively processes nested criteria arrays. Preserves op, value, and values as-is.
 */
// Valid ContactFilterCriteriaProperty values — used to check if a property is already a valid enum value
// (the audienceInfo path sends numeric values like 9 for Tag, while the contactList path sends strings)
const VALID_FILTER_PROPERTIES = new Set<unknown>(Object.values(Api.ContactFilterCriteriaProperty));

function normalizeContactFilterCriteria(criteria: Api.IContactFilterCriteria): Api.IContactFilterCriteria | null {
	const normalized: Api.IContactFilterCriteria = { ...criteria };

	if (criteria.property !== undefined) {
		const mapped = PROPERTY_NAME_TO_ENUM[criteria.property as unknown as string];
		if (mapped !== undefined) {
			normalized.property = mapped;
		} else if (VALID_FILTER_PROPERTIES.has(criteria.property)) {
			// Already a valid enum value (e.g., numeric 9 for Tag) — keep as-is
		} else {
			// Unrecognized property — the AI sometimes sends tag values as property names
			// (e.g., "Financial Services - Angel Investor/VC" instead of "Tag").
			console.warn(`[AI Filter] Stripping unrecognized filter property: ${String(criteria.property)}`);
			if (!criteria.criteria?.length) {
				// Leaf criterion — strip entirely so one bad criterion doesn't cause a 400
				return null;
			}
			// Compound criterion with children — clear the bad property but keep the children
			normalized.property = undefined;
		}
	}

	if (criteria.criteria?.length) {
		normalized.criteria = criteria.criteria
			.map(normalizeContactFilterCriteria)
			.filter((c): c is Api.IContactFilterCriteria => c !== null);
	}

	return normalized;
}

/**
 * Flattens contact filter criteria into a top-level array so that sortContactFilterCriteria
 * can classify each criterion into the correct bucket (searches/filters) for UI display.
 * Without flattening, nested/mixed criteria end up in the "compound" bucket and the filter
 * display at the top of the contacts page and email campaign recipient list won't show them.
 *
 * - And groups are recursively unwrapped (children promoted to top level, since top-level is implicitly And)
 * - Not filters with multiple children are expanded into individual Not entries
 * - Or groups are kept intact — PeopleSearchDescription handles Or groups directly
 *   (e.g., Or(StateProvince:"NY", StateProvince:"CA") renders as "NY, CA")
 * - Other compound criteria are kept intact
 * - Leaf criteria (with property, no nested criteria) are added directly
 */
function flattenContactFilterCriteria(criteria: Api.IContactFilterCriteria[]): Api.IContactFilterCriteria[] {
	const flat: Api.IContactFilterCriteria[] = [];
	for (const filter of criteria) {
		if (filter.op === Api.CONTACT_FILTER_OPERATOR.And && filter.criteria?.length) {
			// Date range compounds (e.g., LastCommunicationDate, CreationDate, KeyDate) use And
			// with Gte/Lte children — these must stay nested so the UI can parse the range.
			const hasRangeOperators = filter.criteria.some(
				c => c.op === Api.CONTACT_FILTER_OPERATOR.Gte || c.op === Api.CONTACT_FILTER_OPERATOR.Lte
			);
			if (hasRangeOperators) {
				flat.push(filter);
			} else {
				// And is associative — promote children to top level (recursively)
				flat.push(...flattenContactFilterCriteria(filter.criteria));
			}
		} else if (filter.op === Api.CONTACT_FILTER_OPERATOR.Not && filter.criteria?.length) {
			for (const criterion of filter.criteria) {
				flat.push({
					op: Api.CONTACT_FILTER_OPERATOR.Not,
					property: criterion.property,
					value: criterion.value,
				});
			}
		} else if (filter.op === Api.CONTACT_FILTER_OPERATOR.Or && filter.criteria?.length) {
			flat.push(filter);
		} else {
			flat.push(filter);
		}
	}
	return flat;
}

/**
 * Builds an IBulkContactsRequest from a ContactListToolCall for use in contacts page navigation.
 * Handles both the new contactFilterRequest format and the legacy SimpleFilter format.
 *
 * Extracts OwnedBy criteria from the filter and moves them to the ownershipFilter field,
 * since PeopleSearchDescription reads ownership from that separate field (not from filter.criteria).
 */
function parseContactListValue(contactList: Api.ContactListToolCall): Api.IBulkContactsRequest | null {
	// The API sends filter data inside the `value` JSON string as:
	// { "filters": "<IBulkContactsRequest JSON>", "totalcount": "12\n" }
	// Parse it out when the typed properties aren't populated.
	if (!contactList.value) {
		return null;
	}
	try {
		const parsed = JSON.parse(contactList.value);
		if (parsed?.filters && typeof parsed.filters === 'string') {
			return JSON.parse(parsed.filters) as Api.IBulkContactsRequest;
		}
	} catch {
		// parse error — fall through
	}
	return null;
}

function buildContactsSearchRequest(contactList: Api.ContactListToolCall): Api.IBulkContactsRequest {
	let criteria: Api.IContactFilterCriteria[];

	if (contactList.contactFilterRequest?.filter) {
		const normalized = normalizeContactFilterCriteria(contactList.contactFilterRequest.filter);
		criteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
	} else {
		// Try parsing the filter from the raw `value` JSON string
		const parsedRequest = parseContactListValue(contactList);
		if (parsedRequest?.filter) {
			const normalized = normalizeContactFilterCriteria(parsedRequest.filter);
			criteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
		} else {
			criteria = (contactList.filters ?? [])
				.map(convertSimpleFilter)
				.filter((c): c is Api.IContactFilterCriteria => c !== undefined);
		}
	}

	const flattened = flattenContactFilterCriteria(criteria);

	// Extract ownership criteria — PeopleSearchDescription reads from ownershipFilter, not filter.criteria
	const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
	const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

	const request: Api.IBulkContactsRequest = {
		filter: { criteria: remainingCriteria },
		ownershipFilter: ownershipCriterion
			? { criteria: ownershipCriterion }
			: { criteria: { property: Api.ContactFilterCriteriaProperty.All } },
	};

	return request;
}

/**
 * Converts a SimpleFilterCriteria to IContactFilterCriteria if the property is supported.
 * Returns undefined if the property is not recognized.
 *
 * Handles special cases:
 * - LastCommunicationDateAfter → LastCommunicationDate with Gte op
 * - LastCommunicationDate with value "never" → LastCommunicationDate with no value
 * - LastCommunicationDate with a date → LastCommunicationDate with Lte op
 */
function convertSimpleFilterCriteria(criteria: {
	property: string;
	value: string;
}): Api.IContactFilterCriteria | undefined {
	// Special case: LastCommunicationDateAfter → LastCommunicationDate with Gte
	if (criteria.property === 'LastCommunicationDateAfter') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			value: criteria.value,
			op: Api.CONTACT_FILTER_OPERATOR.Gte,
		};
	}

	// Special case: LastCommunicationDate with "never" → no value (contacts page "Never" preset)
	if (criteria.property === 'LastCommunicationDate' && criteria.value?.toLowerCase() === 'never') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
		};
	}

	// Special case: LastCommunicationDate with a date → Lte op
	if (criteria.property === 'LastCommunicationDate') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			value: criteria.value,
			op: Api.CONTACT_FILTER_OPERATOR.Lte,
		};
	}

	const enumProperty = SIMPLE_FILTER_PROPERTY_MAP[criteria.property];
	if (enumProperty === undefined) {
		return undefined;
	}
	return {
		property: enumProperty,
		value: criteria.value,
	};
}

/**
 * Converts a full SimpleFilter (with op + criteria[]) into an IContactFilterCriteria.
 * - Maps each criterion via convertSimpleFilterCriteria
 * - Maps SimpleFilter.op to CONTACT_FILTER_OPERATOR values
 * - Single criterion + Not → wraps in { op: Not, criteria: [single] }
 * - Single criterion + And/Or → returns criterion directly
 * - Multiple criteria → wraps in { op, criteria: [...] }
 */
function convertSimpleFilter(filter: Api.SimpleFilter): Api.IContactFilterCriteria | undefined {
	const converted = filter.criteria
		.map(convertSimpleFilterCriteria)
		.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

	if (converted.length === 0) {
		return undefined;
	}

	// Support both `op` (current) and `operator` (legacy, from old stored AI chat messages)
	const rawOp = filter.op ?? filter.operator;
	const mappedOp = rawOp ? SIMPLE_FILTER_OPERATOR_MAP[rawOp] : undefined;

	if (converted.length === 1) {
		// Not requires wrapping even for a single criterion
		if (mappedOp === Api.CONTACT_FILTER_OPERATOR.Not) {
			return {
				op: Api.CONTACT_FILTER_OPERATOR.Not,
				criteria: [converted[0]],
			};
		}
		return converted[0];
	}

	return {
		op: mappedOp ?? Api.CONTACT_FILTER_OPERATOR.And,
		criteria: converted,
	};
}

/**
 * Converts ContactAudienceInfo from the AI to contact selection data for campaign creation.
 * Supports both tagFilters (for dynamic audience) and contactIds (for explicit contacts).
 * Uses convertSimpleFilter to properly handle each filter's op (And/Or/Not).
 *
 * IMPORTANT: The contactFilterRequest must produce a flat criteria array (not deeply nested)
 * because TagEmailRecipientsList checks hasUserSelectedContactFilterSearchCriteria, which
 * classifies criteria via sortContactFilterCriteria. Deeply nested compound criteria
 * (mixing SearchCriteriaProperties and SearchCriteriaFilterProperties) end up in the
 * "compound" bucket, which isn't recognized as user-selected, causing the UI to show
 * "pick audience" instead of the loaded recipients.
 */
function convertAudienceInfoToContactSelection(audienceInfo: Api.ContactAudienceInfo): {
	bulkFilterRequest?: Api.IEmailMessageContactsFilterRequest;
	contactIds?: string[];
} | null {
	// Prefer native contactFilterRequest from AI (new format)
	if (audienceInfo.contactFilterRequest?.filter) {
		const normalized = normalizeContactFilterCriteria(audienceInfo.contactFilterRequest.filter);
		const topCriteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
		const flattened = flattenContactFilterCriteria(topCriteria);

		// Extract ownership criteria — TagEmailRecipientsList reads from ownershipFilter, not filter.criteria
		const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
		const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

		return {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: remainingCriteria,
				},
				...(ownershipCriterion ? { ownershipFilter: { criteria: ownershipCriterion } } : {}),
			},
		};
	}

	// Handle tagFilters - for dynamic audience selection (also includes location filters) [fallback for old format]
	if (audienceInfo.tagFilters?.length) {
		const convertedFilters = audienceInfo.tagFilters
			.map(convertSimpleFilter)
			.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

		if (convertedFilters.length === 0) {
			return null;
		}

		const flattened = flattenContactFilterCriteria(convertedFilters);

		// Extract ownership criteria — TagEmailRecipientsList reads from ownershipFilter, not filter.criteria
		const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
		const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

		return {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: remainingCriteria,
				},
				...(ownershipCriterion ? { ownershipFilter: { criteria: ownershipCriterion } } : {}),
			},
		};
	}

	// Handle contactIds - for explicit contact selection
	if (audienceInfo.contactIds?.length) {
		return {
			contactIds: audienceInfo.contactIds,
		};
	}

	return null;
}

const defaultOnUseAIEmail =
	(fullscreenModal: FullScreenModalViewModel) =>
	(
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		const contactSelection = audienceInfo ? convertAudienceInfoToContactSelection(audienceInfo) : null;

		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						...(contactSelection || {}),
						subject,
						body: content,
						aiReference,
					},
				},
			},
		});
	};
const defaultOnUseSocialContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/social-media/post/create`,
			state: {
				initialContent: content,
				aiReference,
			},
		});
	};
const defaultOnUseBlogContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/blog/post/create`,
			state: {
				initialContent: content,
				initialSubject: subject,
				aiReference,
			},
		});
	};

const UniversalAssistantChatErrorBoundary = ({ children }: { children: ReactNode }) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const conversationDrawerActions = useConversationDrawerStateActions();
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					conversationDrawerActions.startNewChat(UNIVERSAL_ASSISTANT_NAME);
					resetErrorBoundary();
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function DefaultUniversalAssistantChat(props: Partial<UniversalAssistantChatProps>) {
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	return (
		<UniversalAssistantChatErrorBoundary>
			<UniversalAssistantChat
				onUseAIEmail={defaultOnUseAIEmail(fullscreenModal)}
				onUseSocialContent={defaultOnUseSocialContent(fullscreenModal)}
				/* onUseGoogleReviewReply={_reply => {
			}} */
				onUseBlogContent={defaultOnUseBlogContent(fullscreenModal)}
				onUseContact={contactInfo => {
					history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
				}}
				onUseContactList={contactList => {
					const request = buildContactsSearchRequest(contactList);
					const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
					history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
				}}
				onNavigate={(entityType, entityId, parentEntityType, parentEntityId) => {
					const path = getNavigationPath(
						entityType as NavigationEntityType,
						entityId,
						parentEntityType as NavigationEntityType,
						parentEntityId
					);
					history.push(path);
				}}
				{...props}
			/>
		</UniversalAssistantChatErrorBoundary>
	);
}

interface AdminAppAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const AdminAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createUniversalAssistantConversation(
						{
							assistantName: ADMIN_AGENT_NAME,
							impersonationContext,
						},
						{
							onSuccess: newConversation => {
								prependAIConversationToFilterCache(
									{ filter: ADMIN_CONVERSATION_FILTER, impersonationContext },
									newConversation
								);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function AdminAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<AdminAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<AdminAssistantChat {...props} />
		</AdminAssistantChatErrorBoundary>
	);
}

export function AdminAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AdminAppAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: ADMIN_CONVERSATION_FILTER,
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: ADMIN_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createUniversalAssistantConversation]);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({ assistantName: ADMIN_AGENT_NAME });
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
		resetAttachments();
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, how can I help you with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}?
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={aiAssistantStreamingMutation.status}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

interface SurveyBuilderAppAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
}

const SURVEY_BUILDER_STARTER_PROMPTS = [
	'Build a custom survey',
	'Build a customer satisfaction survey',
	'Create an event registration form',
	'Make a quick 3-question feedback form',
	'Clone a sample survey from a template',
];

const SurveyBuilderAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('SurveyBuilderAssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createSurveyBuilderConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SurveyBuilder-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createSurveyBuilderConversation(
						{
							assistantName: SURVEY_BUILDER_AGENT_NAME,
							impersonationContext,
						},
						{
							onSuccess: newConversation => {
								prependAIConversationToFilterCache(
									{ filter: SURVEY_BUILDER_CONVERSATION_FILTER, impersonationContext },
									newConversation
								);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('SurveyBuilderAssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function SurveyBuilderAppAssistantChat(props: SurveyBuilderAppAssistantChatProps) {
	return (
		<SurveyBuilderAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<SurveyBuilderAssistantChat {...props} />
		</SurveyBuilderAssistantChatErrorBoundary>
	);
}

export function SurveyBuilderAssistantChat({ impersonationContext }: SurveyBuilderAppAssistantChatProps) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SurveyBuilderAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const accountImpersonationContext = impersonationContext;
	const accountQuery = useAdminAccountQuery({
		accountId: accountImpersonationContext.account.id,
	});
	const { users: allUsers, isLoading: isLoadingUsers } = useLoadAllUsers({
		impersonationContext: accountImpersonationContext,
		pageSize: 500,
	});
	const activeAdmins = React.useMemo(
		() =>
			allUsers.filter(
				user => user.activationStatus === 'ACTIVE' && (user.role === 'admin' || user.role === 'superAdmin')
			),
		[allUsers]
	);
	const adminStatusMessage = React.useMemo(() => {
		if (isLoadingUsers) return 'Loading admins...';
		if (activeAdmins.length === 0) return 'No active admins available';
		return `${activeAdmins.length} admin${activeAdmins.length === 1 ? '' : 's'} available`;
	}, [activeAdmins.length, isLoadingUsers]);
	const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
	const mainContactUserId = accountQuery.data?.additionalInfo?.mainContactUserId;
	const isAccountLoading = accountQuery.isLoading;
	useEffect(() => {
		if (selectedAdminId !== null || isLoadingUsers || isAccountLoading || activeAdmins.length === 0) {
			return;
		}
		if (mainContactUserId && activeAdmins.some(u => u.id === mainContactUserId)) {
			setSelectedAdminId(mainContactUserId);
			return;
		}
		setSelectedAdminId(activeAdmins[0]?.id ?? null);
	}, [activeAdmins, isLoadingUsers, isAccountLoading, mainContactUserId, selectedAdminId]);
	const selectedAdmin = React.useMemo(
		() => (selectedAdminId ? (activeAdmins.find(u => u.id === selectedAdminId) ?? null) : null),
		[activeAdmins, selectedAdminId]
	);
	const chatImpersonationContext: Api.IImpersonationContext = React.useMemo(
		() =>
			selectedAdmin
				? { account: accountImpersonationContext.account, user: { id: selectedAdmin.id } }
				: accountImpersonationContext,
		[accountImpersonationContext, selectedAdmin]
	);

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: SURVEY_BUILDER_CONVERSATION_FILTER,
		impersonationContext: chatImpersonationContext,
		enabled: Boolean(selectedAdmin),
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(selectedAdmin) && Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext: chatImpersonationContext,
	});
	const { mutate: createSurveyBuilderConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SurveyBuilder-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		if (!selectedAdmin) {
			return;
		}
		createSurveyBuilderConversation({
			assistantName: SURVEY_BUILDER_AGENT_NAME,
			impersonationContext: chatImpersonationContext,
		});
	};
	const chatImpersonationContextRef = useRef(chatImpersonationContext);
	chatImpersonationContextRef.current = chatImpersonationContext;
	useEffect(() => {
		if (selectedAdmin && aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createSurveyBuilderConversation({
				assistantName: SURVEY_BUILDER_AGENT_NAME,
				impersonationContext: chatImpersonationContextRef.current,
			});
		}
	}, [
		selectedAdmin,
		aiConversationFilterQuery.isSuccess,
		aiConversationFilterQuery.data,
		createSurveyBuilderConversation,
	]);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({ assistantName: SURVEY_BUILDER_AGENT_NAME });
	const attachmentsController = useAIChatAttachments({ impersonationContext: chatImpersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;
	const conversationId = aiConversationFilterQuery.data?.[0]?.id;
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, chatImpersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext: chatImpersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const submitText = (text: string) => {
		if (!conversationId || !text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, chatImpersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext: chatImpersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext: chatImpersonationContext,
		});
		resetAttachments();
	};

	const handlePromptSubmit = ({ text }: PromptInputMessage) => submitText(text);

	const renderChatBody = () => {
		if (!selectedAdmin) {
			if (!isLoadingUsers && activeAdmins.length === 0) {
				return (
					<div className={css(s.surveyBuilderGate)}>
						This account has no active admins. A survey can only be built on behalf of an account admin.
					</div>
				);
			}
			return <LoadingSpinner />;
		}
		if (!conversationId || !aiConversationChatMessageQuery.isSuccess) {
			return <LoadingSpinner />;
		}
		const showStarters =
			!aiConversationChatMessageQuery.data?.length &&
			!aiAssistantStreamingMutation.isLoading &&
			!aiAssistantStreamingMutation.isStreaming;
		return (
			<>
				<ChatConversation contextRef={conversationRef}>
					<ChatConversationContent>
						<AIChatAssistantMessage>
							Hi {userSession.user.firstName}, I can help you build a survey for{' '}
							{`${accountQuery.data?.companyName ?? 'this account'}`}. What would you like to create?
						</AIChatAssistantMessage>
						{showStarters ? (
							<div className={css(s.surveyStarters)}>
								{SURVEY_BUILDER_STARTER_PROMPTS.map(prompt => (
									<Button variant='secondary' size='sm' key={prompt} onClick={() => submitText(prompt)}>
										{prompt}
									</Button>
								))}
							</div>
						) : null}
						{aiConversationChatMessageQuery.data?.map((message, i) => {
							return (
								<AIConversationMessage
									key={`${id}-message-${message.id ?? i}`}
									message={message}
									onErrorTryAgain={handleTryAgain}
									conversationId={conversationId}
									impersonationContext={chatImpersonationContext}
								/>
							);
						})}
						{aiAssistantStreamingMutation.isLoading ? (
							<AIChatAssistantMessage isThinking>
								<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
							</AIChatAssistantMessage>
						) : null}
					</ChatConversationContent>
					<ChatConversationScrollButton />
				</ChatConversation>
				<div className={css(s.inputContainer)}>
					<PromptInput
						onSubmit={handlePromptSubmit}
						accept={AI_CHAT_FILE_ACCEPT}
						maxFiles={AI_CHAT_MAX_FILES}
						maxFileSize={AI_CHAT_MAX_FILE_SIZE}
						onError={err => toast.error(err.message)}
						multiple
					>
						<PromptInputAttachments controller={attachmentsController} />
						<PromptInputBody>
							<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
						</PromptInputBody>
						<PromptInputFooter>
							<PromptInputTools>
								<PromptInputAttachmentButton />
								<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
							</PromptInputTools>
							<PromptInputSubmit
								status={aiAssistantStreamingMutation.status}
								onClick={handlePromptSubmitClick}
								disabled={isUploading}
							/>
						</PromptInputFooter>
					</PromptInput>
				</div>
			</>
		);
	};

	return (
		<UniversalAssistantLayout
			onNewChat={selectedAdmin && conversationId ? handleNewChatClick : undefined}
			title='Survey Builder Assistant'
		>
			<div className={css(s.actingOnBehalfBar)}>
				<label className={css(s.actingOnBehalfLabel)} htmlFor='survey-builder-impersonation-combobox'>
					Acting on behalf of
				</label>
				<Combobox
					items={activeAdmins}
					value={selectedAdmin}
					onValueChange={value => {
						const nextId = (value as Api.IUser | null)?.id;
						if (nextId) {
							setSelectedAdminId(nextId);
						}
					}}
					itemToStringLabel={item => getDisplayName(item as Api.IUser) ?? (item as Api.IUser)?.email ?? ''}
					isItemEqualToValue={(a, b) => (a as Api.IUser)?.id === (b as Api.IUser)?.id}
				>
					<ComboboxInput
						id='survey-builder-impersonation-combobox'
						placeholder={isLoadingUsers ? 'Loading admins...' : 'Search admins...'}
						disabled={isLoadingUsers || activeAdmins.length === 0}
					/>
					<ComboboxContent>
						<ComboboxStatus>{adminStatusMessage}</ComboboxStatus>
						<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
			{renderChatBody()}
		</UniversalAssistantLayout>
	);
}

const SuccessMeetingPrepAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('SuccessMeetingPrepAssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createSuccessMeetingPrepConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createSuccessMeetingPrepConversation(
						{
							assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
							impersonationContext,
						},
						{
							onSuccess: newConversation => {
								prependAIConversationToFilterCache(
									{ filter: SUCCESS_MEETING_PREP_CONVERSATION_FILTER, impersonationContext },
									newConversation
								);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('SuccessMeetingPrepAssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function SuccessMeetingPrepAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<SuccessMeetingPrepAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<SuccessMeetingPrepAssistantChat {...props} />
		</SuccessMeetingPrepAssistantChatErrorBoundary>
	);
}

export function SuccessMeetingPrepAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SuccessMeetingPrepAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: SUCCESS_MEETING_PREP_CONVERSATION_FILTER,
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createSuccessMeetingPrepConversation({
				assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createSuccessMeetingPrepConversation]);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
	});
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
		resetAttachments();
	};
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I&apos;ll help you prepare for your meeting with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}.
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit
							status={aiAssistantStreamingMutation.status}
							onClick={handlePromptSubmitClick}
							disabled={isUploading}
						/>
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}
